import React from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress, Alert, Tooltip } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { AgencyModificationsSummary } from '../../_types/risk_pool';
import { agencyModificationsColors } from '..';

interface AgencyModificationsOverviewProps {
  data?: AgencyModificationsSummary;
  isLoading: boolean;
  isFetching: boolean;
  isError?: boolean;
  error?: Error | null;
  updateParams: (params: { policyMods: string; }) => void;
}

const AgencyModificationsOverview: React.FC<AgencyModificationsOverviewProps> = ({ data, isLoading, isFetching, isError, error, updateParams }) => {
  const yesCount = data?.yesCount ?? 0;
  const noCount = data?.noCount ?? 0;
  const yesPercent = data?.yesPercent ?? 0;
  const noPercent = data?.noPercent ?? 0;

  const pieData = [
    {
      id: 0,
      value: yesCount,
      label: t('dashboard.riskPool.agencyModificationsOverview.YES'),
      color: agencyModificationsColors.YES,
    },
    {
      id: 1,
      value: noCount,
      label: t('dashboard.riskPool.agencyModificationsOverview.NO'),
      color: agencyModificationsColors.NO,
    },
  ];

  return (
    <Card className='lex-chart-card'>
      <CardHeader title={t('dashboard.riskPool.agencyModificationsOverview.title')} />
      <CardContent sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {(isLoading || isFetching) && (
          <CircularProgress />
        )}
        {isError && (
          <Alert severity="error">
            {error?.message || t('dashboard.riskPool.agencyModificationsOverview.error')}
          </Alert>
        )}
        {!isLoading && !isFetching && !isError && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 10,
                minHeight: 0,
                width: 'fit-content',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 160, height: 160 }}>
                <PieChart
                  series={[
                    {
                      data: pieData,
                      innerRadius: 50,
                      outerRadius: 80,
                      paddingAngle: 0,
                      cornerRadius: 0,
                      startAngle: 0,
                      endAngle: 360,
                    },
                  ]}
                  slotProps={{
                    legend: { hidden: true },
                  }}
                  onItemClick={(_event, item) => {
                    const dataItem = pieData[item.dataIndex];
                    updateParams({ policyMods: dataItem.id === 0 ? 'YES' : 'NO' });
                  }}
                  width={160}
                  height={160}
                  colors={[agencyModificationsColors.YES, agencyModificationsColors.NO]}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                />
              </Box>
              <Box
                className='legend'
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 7,
                  minHeight: 0,
                  alignItems: 'center',
                }}
              >
                <Tooltip title={t('dashboard.riskPool.agencyModificationsOverview.YESTooltip')} arrow placement="top">
                <Box
                  className='legend-item'
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '16px 1fr',
                    columnGap: 3,
                    rowGap: 1,
                    minHeight: 0,
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => updateParams({ policyMods: 'YES' })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      borderRadius: 9999,
                      width: 8,
                      height: 8,
                      backgroundColor: agencyModificationsColors.YES,
                    }}
                  />
                  <Typography
                    variant='bodyXs'
                    sx={{ color: neutral[700] }}
                  >
                    {pieData[0].label}
                  </Typography>
                  <Box />
                  <Typography variant='subtitleMd'>{yesPercent}%</Typography>
                </Box>
              </Tooltip>
              <Tooltip title={t('dashboard.riskPool.agencyModificationsOverview.NOTooltip')} arrow placement="top">
                <Box
                  className='legend-item'
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '16px 1fr',
                    columnGap: 3,
                    rowGap: 1,
                    minHeight: 0,
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => updateParams({ policyMods: 'NO' })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      borderRadius: 9999,
                      width: 8,
                      height: 8,
                      backgroundColor: agencyModificationsColors.NO,
                    }}
                  />
                  <Typography
                    variant='bodyXs'
                    sx={{ color: neutral[700] }}
                  >
                    {pieData[1].label}
                  </Typography>
                  <Box />
                  <Typography variant='subtitleMd'>{noPercent}%</Typography>
                </Box>
              </Tooltip>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AgencyModificationsOverview;
